import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { posthog } from 'posthog-js';

import { PROJECT_EXPENSES_KEY } from './constants';
import { patchProjectExpense } from './projectExpenses';
import type { RegisterProjectExpenseFormData } from '@/pages/projects/expenses';
import { parseLocaleNumber } from '@/utils';

export const useUpdateProjectExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      expenseData,
    }: {
      id: number;
      expenseData: RegisterProjectExpenseFormData;
    }) => {
      const mappedPayload = {
        projectId: Number(expenseData.project_information.project?.id),
        projectSubContractId: expenseData.project_information.contract?.id
          ? Number(expenseData.project_information.contract?.id)
          : null,
        expenseTypeId: Number(expenseData.expense_details.expenseType?.id),
        supplierId: expenseData.expense_details.supplier?.id
          ? Number(expenseData.expense_details.supplier?.id)
          : null,
        supplierInvoiceNo: expenseData.expense_details.supplierInvoiceNumber
          ? expenseData.expense_details.supplierInvoiceNumber
          : null,
        expenseNo: expenseData.expense_details.expenseNumber
          ? expenseData.expense_details.expenseNumber
          : null,
        date: format(expenseData.expense_details.date, 'yyyy-MM-dd'),
        amountIncludingVat: parseLocaleNumber(
          expenseData.expense_details.amountIncludingVat,
        ),
        vatAmount: parseLocaleNumber(expenseData.expense_details.vatAmount),
        paymentMethodId: Number(expenseData.expense_details.paymentMethod?.id),
        comment: expenseData.expense_details.expenseComment || '',
        isBillable: expenseData.expense_details.billable || false,
        currencyCode:
          (expenseData.expense_details.expenseCurrency?.isoCode as string) ??
          '',
        exchangeRate: expenseData.exchangeRate,
        baseReferenceCurrencyCode: expenseData.legalEntityCurrencyIsoCode ?? '',
        profit: parseLocaleNumber(
          expenseData.project_financials.profitPercentage,
        ),
        salesPrice: parseLocaleNumber(
          expenseData.project_financials.billableAmount,
        ),
      };
      return patchProjectExpense(id, mappedPayload);
    },
    onSuccess: (updatedProjectExpense) => {
      console.debug('Project expense updated:', updatedProjectExpense);
      queryClient.invalidateQueries({
        queryKey: [PROJECT_EXPENSES_KEY, updatedProjectExpense.expenseId],
      });
      posthog.capture('project_expense_updated');
    },
  });
};
